
import { Component, effect, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { ButtonComponent } from '../../components/button/button.component';
import { InputComponent } from '../../components/input/input.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import TYPES from '../../constants/types';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { User } from '../../models/user';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InviteService } from '../../services/invite.service';
import { Invite } from '../../models/invite';

@Component({
  selector: 'app-complete-account',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    MatCheckbox,
    TranslatePipe,
    MatDatepickerModule,
    MatInputModule,
    MatSelectModule
],
  templateUrl: './complete-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './complete-account.component.scss'
})
export class CompleteAccountComponent {
  
  completeAccountForm: FormGroup;
  firstNameControl: UntypedFormControl;
  lastNameControl: UntypedFormControl;
  dialCodeControl: UntypedFormControl;
  phoneNumberControl: UntypedFormControl;
  birthdateControl: UntypedFormControl;
  psychologistCheckControl: UntypedFormControl;
  termsAndConditionsCheckControl: UntypedFormControl;
  privacyPoliciesCheckControl: UntypedFormControl;
  isSaveButtonDisabled:boolean;
  inviteToken: string | null;
  invite: WritableSignal<Invite | null> = signal<Invite | null>(null);
  
  authService: AuthService = inject(AUTH_SERVICE);
  databaseService: DatabaseService<User> = inject(DATABASE_SERVICE);
  inviteService: InviteService<Invite> = inject(InviteService<Invite>);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);

  BUTTON_TYPES: any;
  TABLES: any;
  COUNTRY_GROUPS: any;
  COUNTRIES: any;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router 
  ) {
    this.inviteToken = localStorage.getItem('inviteToken');
    const userEmail = this.authService.user()?.email;
    if(this.inviteToken && userEmail){
      this.COUNTRIES = TYPES.DATABASE.COUNTRY_GROUPS[1].countries;

      effect(() => {
        const phoneNumber = this.extractAndFormatPrefix(this.invite()?.phone_number, this.getFullCleanCodes(this.COUNTRIES));

        this.firstNameControl.patchValue(this.invite()?.first_name);
        this.lastNameControl.patchValue(this.invite()?.last_name);
        this.birthdateControl.patchValue(new Date(this.invite()?.birthdate ?? ''));
        this.dialCodeControl.patchValue(phoneNumber.dialCode);
        this.phoneNumberControl.patchValue(phoneNumber.phoneNumber);
      })

      this.inviteService.getByTokenAndEmail(this.inviteToken, userEmail).then(
        (data) => {
          this.invite.set(data);
        }
      )
    }

    this.firstNameControl = new UntypedFormControl('', [Validators.required]);
    this.lastNameControl = new UntypedFormControl('', [Validators.required]);
    this.birthdateControl = new UntypedFormControl('', [Validators.required]);
    this.dialCodeControl = new UntypedFormControl('', []);
    this.phoneNumberControl = new UntypedFormControl('', []);
    this.psychologistCheckControl = new UntypedFormControl('', []);
    this.termsAndConditionsCheckControl = new UntypedFormControl('', [Validators.requiredTrue]);
    this.privacyPoliciesCheckControl = new UntypedFormControl('', [Validators.requiredTrue]);
    
    this.completeAccountForm = this.formBuilder.group({
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      dialCode: this.dialCodeControl,
      phoneNumber: this.phoneNumberControl,
      birthdate: this.birthdateControl,
      psychologistCheck: this.psychologistCheckControl,
      termsAndConditionsCheck: this.termsAndConditionsCheckControl,
      privacyPoliciesCheck: this.privacyPoliciesCheckControl
    });

    this.isSaveButtonDisabled = this.completeAccountForm.invalid;
    this.completeAccountForm.statusChanges.subscribe(
      (event => {
        this.isSaveButtonDisabled = this.completeAccountForm.invalid;
      })
    );

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.TABLES = TYPES.DATABASE.TABLES;
    this.COUNTRY_GROUPS = TYPES.DATABASE.COUNTRY_GROUPS;
  }

  async saveAccount(){
    if(!this.isFormInvalid()){
      const user: Partial<User> | null = this.authService.user();
      if(user){
        user.first_name = this.completeAccountForm.value.firstName;
        user.last_name = this.completeAccountForm.value.lastName;
        user.phone_number = (this.completeAccountForm.value.dialCode + this.completeAccountForm.value.phoneNumber).replace(/\D/g, '');
        user.birthdate = this.toPostgresDateString(this.completeAccountForm.value.birthdate);
        user.account_registered = true;
        user.terms_accepted = true;
        user.policies_accepted = true;

        const id = user.id;
        if(id){
          try{
            await this.databaseService.update(this.TABLES.USER, id, user);
            await this.authService.updateUser(user);
            if(this.psychologistCheckControl.value){
              localStorage.setItem('requestProfessionalProfile', 'true');
            }
            this.router.navigate(['/home'], { replaceUrl: true });            
          }catch(error:any){
            let message = ''; 
            if(error.toString().includes('users_phone_number_key')){
              message = this.translate.instant('info.phone_number_duplicatad');              
            }else{
              message = this.translate.instant('info.unkonwn_error', { error: error.toString() } );
            }
            const action:string = this.translate.instant('buttons.dismiss');
            this.snackBar.open(message, action);
          }          
        }else{
           this.router.navigate(['/login'], { replaceUrl: true });//TODO: ROUTE TO ERROR PAGE
        }
      }
    }else{
      this.completeAccountForm.markAllAsDirty();
      this.completeAccountForm.markAllAsTouched();
      this.completeAccountForm.updateValueAndValidity();
    }
  }

  goToTermsAndConditions() {
    this.router.navigate(['/terms']);
  }

  goToPrivacyPolicies() {
    this.router.navigate(['/policies']);
  }

  isFormInvalid(): boolean{
    return this.completeAccountForm.invalid 
      || !this.termsAndConditionsCheckControl.value
      || !this.privacyPoliciesCheckControl.value
  }

  private toPostgresDateString(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD
  }

  extractAndFormatPrefix(fullNumber: string | null | undefined, knownCodes: string[]): {dialCode: string, phoneNumber: string} {
    if (!fullNumber) return {dialCode: '', phoneNumber: ''};

    // 1. Remove qualquer caractere não numérico para garantir a comparação
    const cleanNumber = fullNumber.replace(/\D/g, '');

    // 2. Ordena os códigos por tamanho (decrescente).
    // Isso é crucial! Se tivermos EUA (1) e Bahamas (1242),
    // precisamos checar o mais longo primeiro para não dar "falso positivo" no 1.
    const sortedCodes = [...knownCodes].sort((a, b) => b.length - a.length);

    // 3. Procura qual código coincide com o início do número
    const foundCode = sortedCodes.find(code => cleanNumber.startsWith(code));

    if (foundCode) {
      return {
        dialCode: `+${foundCode}`,
        phoneNumber: cleanNumber.replace(foundCode, '')
      };
    }

    // Fallback: Se não achar na lista, retorna vazio ou o "+" genérico
    return {dialCode: '', phoneNumber: ''};
  }

  /**
   * Filtra a lista de países para retornar o DDI + Código de Área/Região concatenados.
   * Ex: De "+1-246", retorna "1246". De "+355", retorna "355".
   * @param countries A lista completa de países.
   * @returns Um array de strings limpas e únicas (ex: ["1246", "355", "93"]).
  */
  getFullCleanCodes(countries: any[]): string[] {
    
    const cleanCodes = countries.map(country => {
        // Usa regex para remover TODOS os caracteres que NÃO são dígitos (+, -, espaço, etc.)
        return country.dial_code.replace(/\D/g, ''); 
    });

    // Remove duplicatas (ex: se o país "EUA" e "Canadá" tiverem o dial_code apenas "+1")
    return Array.from(new Set(cleanCodes)).filter(code => code.length > 0);
  }

  sortCountriesByTranslatedName(countries: any[]){
    return countries.sort(
      (a, b) => {
        return this.translate.instant(a.name).localeCompare(this.translate.instant(b.name))
      }
    )
  }

}
